import apiClient from './client';
import type {
  User,
  Client,
  Store,
  SnsPost,
  SnsPostFile,
  ActivityLog,
  KpiData,
} from './types';

// Auth
export const authApi = {
  login: (username: string, password: string) =>
    apiClient.post<{ user: User }>('/auth/login', { username, password }),
  logout: () => apiClient.post('/auth/logout'),
  me: () => apiClient.get<{ user: User }>('/auth/me'),
};

// Clients
export const clientsApi = {
  list: () => apiClient.get<Client[]>('/clients'),
  get: (id: number) => apiClient.get<Client>(`/clients/${id}`),
  create: (data: Partial<Client>) => apiClient.post<Client>('/clients', data),
  update: (id: number, data: Partial<Client>) =>
    apiClient.put<Client>(`/clients/${id}`, data),
  delete: (id: number) => apiClient.delete(`/clients/${id}`),
};

// Stores
export const storesApi = {
  listByClient: (clientId: number) =>
    apiClient.get<Store[]>(`/clients/${clientId}/stores`),
  create: (clientId: number, data: Partial<Store>) =>
    apiClient.post<Store>(`/clients/${clientId}/stores`, data),
  update: (id: number, data: Partial<Store>) =>
    apiClient.put<Store>(`/stores/${id}`, data),
  delete: (id: number) => apiClient.delete(`/stores/${id}`),
};

// SNS Posts
export const snsPostsApi = {
  list: (params?: Record<string, string | number>) =>
    apiClient.get<SnsPost[]>('/sns-posts', { params }),
  get: (id: number) => apiClient.get<SnsPost>(`/sns-posts/${id}`),
  create: (data: Partial<SnsPost>) =>
    apiClient.post<SnsPost>('/sns-posts', data),
  update: (id: number, data: Partial<SnsPost>) =>
    apiClient.put<SnsPost>(`/sns-posts/${id}`, data),
  delete: (id: number) => apiClient.delete(`/sns-posts/${id}`),
  changeStatus: (id: number, status: string, reason?: string) =>
    apiClient.put<SnsPost>(`/sns-posts/${id}/status`, { status, reason }),
};

// SNS Post Files
export const snsPostFilesApi = {
  upload: (snsPostId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<SnsPostFile>(`/sns-posts/${snsPostId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (id: number) =>
    apiClient.get(`/sns-post-files/${id}/download`, { responseType: 'blob' }),
  thumbnail: (id: number) =>
    `/api/v1/sns-post-files/${id}/thumbnail`,
  delete: (id: number) => apiClient.delete(`/sns-post-files/${id}`),
  reorder: (snsPostId: number, fileIds: number[]) =>
    apiClient.put(`/sns-posts/${snsPostId}/files/reorder`, { file_ids: fileIds }),
};

// Dashboard
export const dashboardApi = {
  kpi: () => apiClient.get<KpiData>('/dashboard/kpi'),
  upcoming: () => apiClient.get<SnsPost[]>('/dashboard/upcoming'),
};

// Reports
export const reportsApi = {
  monthly: (params: { client_id?: number; year: number; month: number }) =>
    apiClient.get('/reports/monthly', { params }),
  downloadCsv: (params: { client_id?: number; year: number; month: number }) =>
    apiClient.get('/reports/monthly/download', {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    }),
};
